import { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { useNavigate } from '@tanstack/react-location';
import { isFunction } from 'lodash-es';
import { PaymentOrigin, PaymentOriginEnum } from '@frontend/api-invoices';
import {
  CreatePaymentIntentBody,
  createOBPPaymentIntent,
  getPaymentClientSecretWithAuthToken,
} from '@frontend/api-weave-pay';
import { HttpError, useFetch } from '@frontend/fetch';
import {
  PaymentConfigName,
  usePaymentMethodConfig,
  stripeAppearance as appearance,
  useStripePromise,
} from '@frontend/payments-stripe-wrapper';
import { genUUIDV4 } from '@frontend/string';
import { ContentLoader } from '@frontend/design-system';
import { FailureHandler, PaymentPortal, PortalDisabledPage, SuccessHandler } from '../';
import { useLocationPortal } from '../../hooks';
import { Payment, PortalDisabledTypes } from '../../types';
import { convertToCents } from '../../utils';

interface WeaveBillPayProps {
  locationSlug: string;
  amount?: number;
  amountEditable?: boolean;
  showLocationInfo?: boolean;
  paymentConfigName?: PaymentConfigName;
  originType?: PaymentOriginEnum;
  captchaToken?: string | null;
  onSuccess?: SuccessHandler;
  onFailure?: FailureHandler;
}

export const isStripeCardError = (result: any): result is StripeError =>
  typeof result === 'object' && !!result && 'error' in result;

export const WeaveBillPay = ({
  locationSlug,
  amount,
  amountEditable = true,
  showLocationInfo = true,
  paymentConfigName,
  originType = PaymentOrigin.OnlineBillPay,
  captchaToken = null,
  onSuccess,
  onFailure,
}: WeaveBillPayProps) => {
  const { setLocationIdHeader } = useFetch();
  const navigate = useNavigate();
  const { stripePromise } = useStripePromise();
  const paymentMethodConfiguration = usePaymentMethodConfig({ paymentConfigName });
  const { portalData } = useLocationPortal({ locationSlug, onFailure });
  const [isEnabled, setIsEnabled] = useState(true);

  const setupPaymentIntent = async (payment: Payment, token: string | null) => {
    setLocationIdHeader(portalData.id);
    const requestBody: CreatePaymentIntentBody = {
      amount: convertToCents(payment?.amount || 0),
      cardholderName: payment?.cardholderName || '',
      origin: originType,
      person: {
        // we don't have person id in case of OBP
        // because it's anonymous payment.
        email: payment?.receiptEmail || '',
        firstName: payment.patientName?.firstName,
        lastName: payment.patientName?.lastName,
      },
      paymentId: genUUIDV4(),
      paymentMethodType: payment?.paymentMethodType,
    };

    let clientSecret;
    let paymentId;
    if (!token) {
      throw new Error('Captcha token is required for online bill pay');
    }

    try {
      const {
        paymentToken,
        paymentId: paymentIdOnSuccess,
        links: { self: paymentIntentUrl },
      } = await createOBPPaymentIntent({ id: portalData.id, requestBody, captchaToken: token });

      const { paymentIntent } = await getPaymentClientSecretWithAuthToken({
        url: paymentIntentUrl,
        authToken: paymentToken,
        locationId: portalData.id,
      });
      clientSecret = paymentIntent.clientSecret;
      paymentId = paymentIdOnSuccess;
    } catch (err) {
      console.error('Error creating payment intent', err);

      if (err instanceof HttpError) {
        if (err.status === 429) {
          setIsEnabled(false);
          return {};
        }
      }

      if (!isFunction(onFailure)) navigate({ to: '/error' });
      else if (err instanceof HttpError || isStripeCardError(err) || err instanceof Error) onFailure(err);
    }
    return { clientSecret, paymentId };
  };

  useEffect(() => {
    if (portalData.onlineBillPayEnabled) {
      setIsEnabled(true);
    } else {
      setIsEnabled(false);
    }
  }, [portalData.onlineBillPayEnabled]);

  if (!portalData.onBehalfOf) return <ContentLoader show={true} />;

  return isEnabled ? (
    <Elements
      stripe={stripePromise || null}
      options={{
        mode: 'payment',
        amount: 50,
        currency: 'usd',
        setupFutureUsage: undefined,
        appearance,
        onBehalfOf: portalData.onBehalfOf,
        paymentMethodConfiguration,
        paymentMethodOptions: {
          us_bank_account: {
            financial_connections: {
              permissions: ['payment_method', 'balances'],
            },
          },
        },
      }}
    >
      <PaymentPortal
        locationData={portalData}
        setupPaymentIntent={setupPaymentIntent}
        amountEditable={amountEditable}
        requestedAmount={amount}
        showLocationInfo={showLocationInfo}
        onSuccess={onSuccess}
        onFailure={onFailure}
        captchaToken={captchaToken}
      />
    </Elements>
  ) : (
    <PortalDisabledPage
      locationSlug={locationSlug}
      reason={!portalData.onlineBillPayEnabled ? PortalDisabledTypes.Disabled : PortalDisabledTypes.RateLimit}
    />
  );
};

export default WeaveBillPay;
